import MoreVertIcon from "@material-ui/icons/MoreVert";
import { deleteAccountType, getAccountTypes } from "apis";
import { Intro, Loader, PopOverMenu, Text } from "components/commons";
import { ConfirmModal } from "components/modals";
import { UserContext } from "contexts";
import { Path, Portal, Role } from "enums";
import AccountType from "enums/account-type";
import { useApi, useModal, useMount } from "hooks";
import locale from "localization";
import React, { useCallback, useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
    formatAmount,
    formatNumber,
    formatVolume,
    handleRequest,
    prettifyRedeemLimitType,
} from "utils";
import { ACCOUNT_TYPE_GROUP_OPTIONS } from "../account-type-vip-details/account-type-vip-details.state";
import styles from "./account-types-vip-list.module.scss";

const StackedPointsCell = ({ points, cap, capTemplate }) => (
  <>
    <p className={styles.mainValue}>{`${points != null ? formatNumber(points, 0) : "--"} pts`}</p>
    <p className={styles.subValue}>
      {locale.populate(capTemplate, [cap != null ? formatNumber(cap, 0) : "--"])}
    </p>
  </>
);

const AccountTypesVipListModule = () => {
  const history = useHistory();
  const confirmModal = useModal();
  const { close } = confirmModal;

  const { user } = useContext(UserContext);
  const { role } = user?.userAccesses?.find((element) => {
    return element.portal === Portal.LOCQ;
  });

  const {
    request: getAccountTypesRequest,
    loading: fetchingAccountTypes,
    result: getAccountTypesResult = { accountTypes: [] },
  } = useApi({
    api: getAccountTypes,
    pageError: false,
  });

  const fetchAccountTypes = useCallback(() => {
    getAccountTypesRequest({ page: 1, perPage: 200 });
  }, [getAccountTypesRequest]);

  useMount(() => {
    fetchAccountTypes();
  });

  const deleteAccountStatusRequest = useApi({
    api: deleteAccountType,
    handleOwnError: {
      badrequest: true,
    },
  });

  const submitDeleteAccountType = useCallback(
    (params) => {
      handleRequest(async () => {
        const result = await deleteAccountStatusRequest?.request({
          ...params,
        });
        if (result) {
          fetchAccountTypes();
        }
      });
    },
    [deleteAccountStatusRequest, fetchAccountTypes]
  );

  const handleDeleteAccountType = useCallback(
    (params, name) => {
      confirmModal.show({
        title: locale.deleteAccountTypeQuestion,
        content: <locale.Populate text={locale.deleteAccountTypeContent} items={[<b>{name}</b>]} />,
        secondary: {
          text: locale.Cancel,
        },
        primary: {
          text: locale.yesDeleteIt,
          onClick: async () => {
            submitDeleteAccountType(params);
            close();
          },
        },
      });
    },
    [close, confirmModal, submitDeleteAccountType]
  );

  const groupedRows = useMemo(() => {
    const { accountTypes = [] } = getAccountTypesResult;
    const filtered = accountTypes.filter(
      (a) => a.accountTypeGroup && a.tierLevel != null
    );

    const byGroup = new Map();
    filtered.forEach((a) => {
      const list = byGroup.get(a.accountTypeGroup) || [];
      list.push(a);
      byGroup.set(a.accountTypeGroup, list);
    });

    const groups = [];
    byGroup.forEach((tiers, groupKey) => {
      const sortedTiers = [...tiers].sort(
        (x, y) => (x.tierLevel ?? 0) - (y.tierLevel ?? 0)
      );
      groups.push({
        groupKey,
        groupLabel:
          sortedTiers.find((a) => a.accountTypeGroupName)?.accountTypeGroupName ||
          ACCOUNT_TYPE_GROUP_OPTIONS.find((option) => option.value === groupKey)?.label ||
          groupKey,
        tiers: sortedTiers,
      });
    });

    return groups;
  }, [getAccountTypesResult]);

  return (
    <div className={styles.container}>
      <ConfirmModal
        {...confirmModal}
        titleStyle={styles.modalTitle}
        contentStyle={styles.modalContent}
      />
      <Intro
        titleStyle={styles.titleStyle}
        textStyle={styles.textStyle}
        title={locale.accountTypes}
        subtitle={locale.viewCreateAccountTypes}
        {...(role !== Role.Accounting && {
          actionText: locale.addNewAccountType,
          actionOnClick: () => {
            history.push(Path.AddAccountType);
          },
        })}
      />

      <div className={styles.tableWrapper}>
        <Loader open={fetchingAccountTypes || deleteAccountStatusRequest?.loading} />
        {!fetchingAccountTypes && groupedRows.length === 0 ? (
          <div className={styles.emptyState}>{locale.sorryNoResultFound}</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th rowSpan={2} className={styles.stickyHeader1}>
                  {locale.accountType}
                </th>
                <th rowSpan={2} className={styles.stickyHeader2}>
                  {locale.tierName}
                </th>
                <th rowSpan={2}>{locale.earningRate}</th>
                <th className={styles.groupHeaderCenter} colSpan={3}>
                  {locale.pointsEarningLimits}
                </th>
                <th className={styles.groupHeaderCenter} colSpan={3}>
                  {locale.pointsRedemptionLimits}
                </th>
                <th rowSpan={2}>{locale.pesoWalletSize}</th>
                <th rowSpan={2}>{locale.fuelTankSize}</th>
                <th className={styles.groupHeaderCenter} colSpan={2}>
                  {locale.cashInLimit}
                </th>
                <th className={styles.groupHeaderCenter} colSpan={2}>
                  {locale.buyFuelLimit}
                </th>
                <th className={styles.groupHeaderCenter} colSpan={2}>
                  {locale.payWithPesoLimit}
                </th>
                <th rowSpan={2} className={styles.wideHeader}>
                  {locale.dailyRedemptionLiterLimit}
                </th>
                <th rowSpan={2}>{locale.actions}</th>
              </tr>
              <tr>
                <th className={styles.subHeader}>{locale.daily}</th>
                <th className={styles.subHeader}>{locale.weekly}</th>
                <th className={styles.subHeader}>{locale.monthly}</th>
                <th className={styles.subHeader}>{locale.daily}</th>
                <th className={styles.subHeader}>{locale.weekly}</th>
                <th className={styles.subHeader}>{locale.monthly}</th>
                <th className={styles.subHeader}>{locale.daily}</th>
                <th className={styles.subHeader}>{locale.monthly}</th>
                <th className={styles.subHeader}>{locale.daily}</th>
                <th className={styles.subHeader}>{locale.monthly}</th>
                <th className={styles.subHeader}>{locale.daily}</th>
                <th className={styles.subHeader}>{locale.monthly}</th>
              </tr>
            </thead>
            <tbody>
              {groupedRows.map((group) =>
                group.tiers.map((a, tierIdx) => (
                  <tr key={`${group.groupKey}-${a.accountTypeId}`}>
                    {tierIdx === 0 && (
                      <td
                        className={`${styles.groupCell} ${styles.stickyCol1}`}
                        rowSpan={group.tiers.length}
                      >
                        <Text className={styles.tierName}>{group.groupLabel}</Text>
                      </td>
                    )}
                    <td className={styles.stickyCol2}>
                      <Text className={styles.tierName}>{a.name || a.tierCode || "--"}</Text>
                    </td>
                    <td>
                      <p className={styles.rateFuel}>
                        {a.fuelEarningLitersPerPoint != null
                          ? locale.populate(locale.fuelEarningRateFormat, [
                              formatNumber(a.fuelEarningLitersPerPoint, 0),
                            ])
                          : locale.fuelEarningRateUnknown}
                      </p>
                      <p className={styles.rateNonfuel}>
                        {a.nonfuelEarningPesoPerPoint != null
                          ? locale.populate(locale.nonfuelEarningRateFormat, [
                              formatAmount(a.nonfuelEarningPesoPerPoint),
                            ])
                          : locale.nonfuelEarningRateUnknown}
                      </p>
                    </td>
                    <td className={styles.subCell}>
                      <StackedPointsCell
                        points={a.pointsEarnDaily}
                        cap={a.pointsEarnTxnCapDaily}
                        capTemplate={locale.upToTransactions}
                      />
                    </td>
                    <td className={styles.subCell}>
                      <StackedPointsCell
                        points={a.pointsEarnWeekly}
                        cap={a.pointsEarnTxnCapWeekly}
                        capTemplate={locale.upToTransactions}
                      />
                    </td>
                    <td className={styles.subCell}>
                      <StackedPointsCell
                        points={a.pointsEarnMonthly}
                        cap={a.pointsEarnTxnCapMonthly}
                        capTemplate={locale.upToTransactions}
                      />
                    </td>
                    <td className={styles.subCell}>
                      <StackedPointsCell
                        points={a.pointsRedeemDaily}
                        cap={a.pointsRedeemCapDaily}
                        capTemplate={locale.upToRedemptions}
                      />
                    </td>
                    <td className={styles.subCell}>
                      <StackedPointsCell
                        points={a.pointsRedeemWeekly}
                        cap={a.pointsRedeemCapWeekly}
                        capTemplate={locale.upToRedemptions}
                      />
                    </td>
                    <td className={styles.subCell}>
                      <StackedPointsCell
                        points={a.pointsRedeemMonthly}
                        cap={a.pointsRedeemCapMonthly}
                        capTemplate={locale.upToRedemptions}
                      />
                    </td>
                    <td>
                      <Text className={styles.mainValue}>{formatAmount(a.walletSize)}</Text>
                    </td>
                    <td>
                      <Text className={styles.mainValue}>{formatVolume(a.fuelTankSize)}</Text>
                    </td>
                    <td className={styles.subCell}>
                      <Text className={styles.mainValue}>
                        {formatAmount(a.totalCashinLimitDaily)}
                      </Text>
                    </td>
                    <td className={styles.subCell}>
                      <Text className={styles.mainValue}>
                        {formatAmount(a.totalCashinLimitMonthly)}
                      </Text>
                    </td>
                    <td className={styles.subCell}>
                      <Text className={styles.mainValue}>
                        {formatVolume(a.totalBuyFuelLimitDaily)}
                      </Text>
                    </td>
                    <td className={styles.subCell}>
                      <Text className={styles.mainValue}>
                        {formatVolume(a.totalBuyFuelLimitMonthly)}
                      </Text>
                    </td>
                    <td className={styles.subCell}>
                      <Text className={styles.mainValue}>
                        {formatAmount(a.totalPayWithPesoLimitDaily)}
                      </Text>
                    </td>
                    <td className={styles.subCell}>
                      <Text className={styles.mainValue}>
                        {formatAmount(a.totalPayWithPesoLimitMonthly)}
                      </Text>
                    </td>
                    <td className={styles.subCell}>
                      <Text className={styles.mainValue}>{`${
                        a.totalRedeemLimitDaily
                      } - ${prettifyRedeemLimitType(a.totalRedeemLimitType)}`}</Text>
                      <Text className={styles.subValue}>
                        {formatVolume(a.totalRedemptionLiterLimitDaily)}
                      </Text>
                    </td>
                    <td className={styles.actionsCell}>
                      <PopOverMenu
                        options={[
                          {
                            content: locale.viewDetails,
                            onClick: () => {
                              history.push(Path.ViewAccountType, {
                                accountTypeId: a.accountTypeId,
                              });
                            },
                          },
                          {
                            removable: true,
                            content: locale.editAccountType,
                            disabled: role === Role.Accounting,
                            onClick: () => {
                              history.push(Path.EditAccountType, {
                                accountTypeId: a.accountTypeId,
                              });
                            },
                          },
                          {
                            removable: true,
                            content: locale.deleteAccountType,
                            onClick: () => {
                              handleDeleteAccountType(
                                {
                                  accountTypeId: a.accountTypeId,
                                },
                                a.name
                              );
                            },
                            disabled:
                              a.type === AccountType.TNVS ||
                              a.isDefault ||
                              role === Role.Accounting,
                          },
                        ]}
                      >
                        <MoreVertIcon className={styles.icon} />
                      </PopOverMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AccountTypesVipListModule;
